import { useMemo, useState } from 'react';
import { GiHamburgerMenu as MenuIcon } from "react-icons/gi";
import { MdOutlineClose as CloseIcon } from "react-icons/md";
import { NavLink, useNavigate } from 'react-router-dom';
import { ReactComponent as ReportTicket } from '@Assets/report_ticket_sidepanel.svg';
import { ReactComponent as TrackTicket } from '@Assets/track_ticket_sidepanel.svg';
import { ReactComponent as HomeIcon } from '@Assets/home_sidepanel.svg';
import { HelpCircle, LogOut } from 'lucide-react';
import { useMediaQuery } from '@mui/material';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './SidePanel.scss';


export default function SidePanel(props) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { activeUser, userType } = useReduxActiveUser();
    const className = 'sidePanel';

  const isLoggedIn = !!activeUser && Object.keys(activeUser).length > 0 && activeUser?.id > 0;
  const userName = activeUser?.fullName || activeUser?.userName || 'User';
  const userEmail = activeUser?.email || '';

  const initials = useMemo(() => {
    const parts = (userName || 'U').trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
    return (first + (last || '')).toUpperCase();
  }, [userName]);

  const navItems = [
    { to: '/home', label: 'Home', icon: HomeIcon },
    { to: '/home/track', label: 'Track Ticket', icon: TrackTicket },
    { to: '/home/form/search', label: 'Report Ticket', icon: ReportTicket },
  ];

  const closeMobile = () => setMobileOpen(false);
  const navItem = ({ isActive }) => `${className}__nav__item${isActive ? ' is-active' : ''}`;

  const sidebar = (
    <aside className={className + '__sidebar'}>
      <span className={className + '__label'}>Workspace</span>

      <nav className={className + '__nav'}>
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={navItem}
              onClick={closeMobile}
            >
              <span className={className + '__nav__icon'}><Icon /></span>
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className={className + '__spacer'} />

      <NavLink to="/support" className={className + '__help'} onClick={closeMobile}>
        <HelpCircle size={15} />
        <span>Help & Support</span>
      </NavLink>

      {isLoggedIn ? (
        <div className={className + '__profile'}>
          <div className={className + '__profile__avatar'}>
            {activeUser?.photo && activeUser.photo !== 'null' && activeUser.photo !== ''
              ? <img src={activeUser.photo} alt={userName} />
              : <span>{initials}</span>
            }
          </div>
          <div className={className + '__profile__info'}>
            <span className={className + '__profile__name'} title={userName}>{userName}</span>
            <span className={className + '__profile__role'}>
              {userType === 'reporter' ? 'Reporter' : 'User'}
            </span>
            {!!userEmail && <span className={className + '__profile__email'} title={userEmail}>{userEmail}</span>}
          </div>
          <button
            className={className + '__profile__logout'}
            onClick={() => {
              closeMobile();
              navigate('/logout');
            }}
            title="Logout"
            aria-label="Logout"
          >
            <LogOut size={14} />
          </button>
        </div>
      ) : (
        <div className={className + '__guest'}>
          <div className={className + '__guest__title'}>Welcome to KaizIQ Resolve</div>
          <div className={className + '__guest__text'}>
            Login to report and track support tickets from one place.
          </div>
          <button
            className={className + '__guest__cta'}
            onClick={() => {
              closeMobile();
              navigate('/login');
            }}
          >
            Login
          </button>
        </div>
      )}
    </aside>
  );

  if (isMobile) {
    return (
      <div className={className}>
        <button
          className={className + '__toggle'}
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
        </button>

        {mobileOpen && (
          <>
            <div className={className + '__overlay'} onClick={closeMobile} />
            {sidebar}
          </>
        )}
      </div>
    );
  }

  return <div className={className}>{sidebar}</div>;
}